/**
 * Export a DOM element to a multi-page A4 PDF.
 *
 * Captures the element as it is rendered (dark theme + SVG charts intact) with
 * html2canvas, then slices the tall bitmap across A4 pages with jsPDF. Both libs
 * are dynamically imported so they stay out of the main bundle until first use.
 *
 * Elements marked `data-export-hide` (e.g. the export button itself) are skipped.
 */
export async function exportElementToPdf(
  el: HTMLElement,
  opts: { filename: string; bg?: string },
): Promise<void> {
  // html2canvas-pro: drop-in fork that supports modern CSS color funcs
  // (color-mix(), oklch(), lab()) — the nocturne UI uses color-mix(), which the
  // original html2canvas 1.4.1 cannot parse (it throws and the export fails).
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import("html2canvas-pro"),
    import("jspdf"),
  ]);

  // make sure the icon/heading web fonts are painted before we snapshot
  try { await (document as any).fonts?.ready; } catch { /* ignore */ }

  const bg = opts.bg ?? "#161826";
  const canvas = await html2canvas(el, {
    backgroundColor: bg,
    scale: 2,                 // crisp but safely under browser canvas-size limits
    useCORS: true,
    logging: false,
    ignoreElements: (node) => node.getAttribute?.("data-export-hide") != null,
  });

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const imgW = pageW - margin * 2;
  const pxPerPt = canvas.width / imgW;            // canvas px per PDF pt
  const sliceMaxPx = Math.floor((pageH - margin * 2) * pxPerPt); // page body height in canvas px

  let offsetPx = 0;
  let firstPage = true;
  while (offsetPx < canvas.height) {
    const sliceHpx = Math.min(sliceMaxPx, canvas.height - offsetPx);
    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = sliceHpx;
    const ctx = slice.getContext("2d");
    if (!ctx) break;
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, offsetPx, canvas.width, sliceHpx, 0, 0, canvas.width, sliceHpx);

    if (!firstPage) pdf.addPage();
    pdf.setFillColor(bg);
    pdf.rect(0, 0, pageW, pageH, "F");
    pdf.addImage(slice.toDataURL("image/jpeg", 0.92), "JPEG", margin, margin, imgW, sliceHpx / pxPerPt);

    offsetPx += sliceHpx;
    firstPage = false;
  }

  pdf.save(opts.filename);
}

/** `bao-cao-phan-phoi-lead_2026-10-30.pdf` */
export function stampedFilename(base: string): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${base}_${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}.pdf`;
}
